/**
 * React Router configuration — mirrors Next.js App Router structure.
 *
 * Layout hierarchy:
 *   RootLayout (Providers + Outlet)
 *   ├── GuestGuard → AuthLayout → /login, /verify, /sign-in
 *   ├── AuthGuard → DashboardLayout → all dashboard routes
 *   │   ├── SettingsLayout → /settings/*
 *   │   ├── AdminLayout → /admin/*
 *   │   │   ├── AccessLayout → /admin/access/*
 *   │   │   ├── BillingLayout → /admin/billing/*
 *   │   │   ├── CdrLayout → /admin/cdr/*
 *   │   │   ├── CrmLayout → /admin/crm/*
 *   │   │   ├── DocumentsLayout → /admin/documents/*
 *   │   │   ├── BackendLayout → /admin/backend/*
 *   │   │   ├── LimsLayout → /admin/lims/*
 *   │   │   ├── MediaLayout → /admin/media/*
 *   │   │   ├── UsersLayout → /admin/users/*
 *   │   │   │   └── UserDetailLayout → /admin/users/:id/*
 *   │   │   └── WalletLayout → /admin/wallet/*
 *   │   ├── WalletLayout → /wallet/*
 *   │   ├── DocumentsLayout → /documents/*
 *   │   ├── HRLayout → /hr/*
 *   │   │   ├── RecruitmentLayout → /hr/recruitment/*
 *   │   │   └── RotationsLayout → /hr/rotations/*
 *   │   └── (flat routes: chat, calls, contacts, etc.)
 *   └── PublicCallLayout → /call/*
 *       PublicLinkLayout → /l/*
 *
 * All page components use `lazy` imports for code splitting.
 */
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { RootLayout } from './layouts/root-layout'
import { AuthGuard, GuestGuard } from './guards'

// Helper to convert Next.js page.tsx default export to React Router lazy format
const p = (importFn: () => Promise<any>) => ({
  lazy: async () => {
    const mod = await importFn()
    return { Component: mod.default || mod.Component || mod }
  },
})

// Helper for layout routes (same pattern)
const l = (importFn: () => Promise<any>) => ({
  lazy: async () => {
    const mod = await importFn()
    return { Component: mod.default || mod.Component || mod }
  },
})

export const router = createBrowserRouter([
  {
    // Root layout — wraps everything with Providers
    element: <RootLayout />,
    children: [
      // ─── Root redirect ────────────────────────────────────────────
      {
        index: true,
        element: <Navigate to="/chat" replace />,
      },

      // ─── Auth routes (GuestGuard) ────────────────────────────────
      {
        element: <GuestGuard />,
        children: [
          {
            ...l(() => import('./app/(auth)/layout')),
            children: [
              { path: 'login', ...p(() => import('./app/(auth)/login/page')) },
              { path: 'verify', ...p(() => import('./app/(auth)/verify/page')) },
              { path: 'sign-in', ...p(() => import('./app/(auth)/sign-in/page')) },
              { path: 'v1/auth/oauth/dc-bank/callback', ...p(() => import('./app/(auth)/oauth/callback/page')) },
            ],
          },
        ],
      },

      // ─── Dashboard routes (AuthGuard) ─────────────────────────────
      {
        element: <AuthGuard />,
        children: [
          {
            ...l(() => import('./app/(dashboard)/layout')),
            children: [
              // Dashboard index → redirect to chat
              { index: true, element: <Navigate to="/chat" replace /> },
              { path: 'dashboard', element: <Navigate to="/chat" replace /> },
              { path: 'home', ...p(() => import('./app/(dashboard)/home/page')) },
              { path: 'about', ...p(() => import('./app/(dashboard)/about/page')) },

              // ─── Chat ───────────────────────────────────────────
              { path: 'chat', ...p(() => import('./app/(dashboard)/chat/page')) },
              { path: 'chat/create-channel/:channelType', ...p(() => import('./app/(dashboard)/chat/create-channel/[channelType]/page')) },
              { path: 'chat/:channelId', ...p(() => import('./app/(dashboard)/chat/[channelId]/page')) },

              // ─── Calls ──────────────────────────────────────────
              { path: 'calls', ...p(() => import('./app/(dashboard)/calls/page')) },
              { path: 'calls/active/:id', ...p(() => import('./app/(dashboard)/calls/active/[id]/page')) },
              { path: 'calls/history', ...p(() => import('./app/(dashboard)/calls/history/page')) },
              { path: 'calls/history/:id', ...p(() => import('./app/(dashboard)/calls/history/[id]/page')) },

              // ─── Conference ─────────────────────────────────────
              { path: 'conference', ...p(() => import('./app/(dashboard)/conference/page')) },
              { path: 'conference/:id', ...p(() => import('./app/(dashboard)/conference/[id]/page')) },
              { path: 'conference/link/:id', ...p(() => import('./app/(dashboard)/conference/link/[id]/page')) },
              { path: 'conference/history', ...p(() => import('./app/(dashboard)/conference/history/page')) },
              { path: 'conference/recordings', ...p(() => import('./app/(dashboard)/conference/recordings/page')) },
              { path: 'conference/schedule', ...p(() => import('./app/(dashboard)/conference/schedule/page')) },

              // ─── Contacts ───────────────────────────────────────
              { path: 'contacts', ...p(() => import('./app/(dashboard)/contacts/page')) },

              // ─── Notifications ──────────────────────────────────
              { path: 'notifications', ...p(() => import('./app/(dashboard)/notifications/page')) },

              // ─── Documents ──────────────────────────────────────
              {
                path: 'documents',
                ...l(() => import('./app/(dashboard)/documents/layout')),
                children: [
                  { index: true, ...p(() => import('./app/(dashboard)/documents/page')) },
                  { path: 'approvals', ...p(() => import('./app/(dashboard)/documents/approvals/page')) },
                  { path: 'chat', ...p(() => import('./app/(dashboard)/documents/chat/page')) },
                ],
              },

              // ─── Storage ────────────────────────────────────────
              { path: 'storage', ...p(() => import('./app/(dashboard)/storage/page')) },
              { path: 'storage/trash', ...p(() => import('./app/(dashboard)/storage/trash/page')) },
              { path: 'storage/activity', ...p(() => import('./app/(dashboard)/storage/activity/page')) },
              { path: 'storage/shared', ...p(() => import('./app/(dashboard)/storage/shared/page')) },
              { path: 'storage/shared/incoming', ...p(() => import('./app/(dashboard)/storage/shared/incoming/page')) },
              { path: 'storage/shared/outgoing', ...p(() => import('./app/(dashboard)/storage/shared/outgoing/page')) },
              { path: 'storage/starred', ...p(() => import('./app/(dashboard)/storage/starred/page')) },
              { path: 'storage/permissions', ...p(() => import('./app/(dashboard)/storage/permissions/page')) },
              { path: 'storage/quota', ...p(() => import('./app/(dashboard)/storage/quota/page')) },
              { path: 'storage/search', ...p(() => import('./app/(dashboard)/storage/search/page')) },
              { path: 'storage/recent', ...p(() => import('./app/(dashboard)/storage/recent/page')) },

              // ─── Links ──────────────────────────────────────────
              { path: 'links', ...p(() => import('./app/(dashboard)/links/page')) },
              { path: 'links/analytics', ...p(() => import('./app/(dashboard)/links/analytics/page')) },
              { path: 'links/conversions', ...p(() => import('./app/(dashboard)/links/conversions/page')) },
              { path: 'links/list', ...p(() => import('./app/(dashboard)/links/list/page')) },
              { path: 'links/qr', ...p(() => import('./app/(dashboard)/links/qr/page')) },
              { path: 'links/slugs', ...p(() => import('./app/(dashboard)/links/slugs/page')) },
              { path: 'links/templates', ...p(() => import('./app/(dashboard)/links/templates/page')) },

              // ─── Wallet ─────────────────────────────────────────
              {
                path: 'wallet',
                ...l(() => import('./app/(dashboard)/wallet/layout')),
                children: [
                  { index: true, ...p(() => import('./app/(dashboard)/wallet/page')) },
                  { path: 'deductions', ...p(() => import('./app/(dashboard)/wallet/deductions/page')) },
                  { path: 'payment-methods', ...p(() => import('./app/(dashboard)/wallet/payment-methods/page')) },
                  { path: 'topup', ...p(() => import('./app/(dashboard)/wallet/topup/page')) },
                  { path: 'transactions', ...p(() => import('./app/(dashboard)/wallet/transactions/page')) },
                  { path: 'transfers', ...p(() => import('./app/(dashboard)/wallet/transfers/page')) },
                  { path: 'withdraw', ...p(() => import('./app/(dashboard)/wallet/withdraw/page')) },
                ],
              },

              // ─── Settings ───────────────────────────────────────
              {
                path: 'settings',
                ...l(() => import('./app/(dashboard)/settings/layout')),
                children: [
                  { index: true, ...p(() => import('./app/(dashboard)/settings/page')) },
                  { path: 'profile', ...p(() => import('./app/(dashboard)/settings/profile/page')) },
                  { path: 'appearance', ...p(() => import('./app/(dashboard)/settings/appearance/page')) },
                  { path: 'language', ...p(() => import('./app/(dashboard)/settings/language/page')) },
                  { path: 'notifications', ...p(() => import('./app/(dashboard)/settings/notifications/page')) },
                  { path: 'voice-video', ...p(() => import('./app/(dashboard)/settings/voice-video/page')) },
                  { path: 'file-media', ...p(() => import('./app/(dashboard)/settings/file-media/page')) },
                  { path: 'help', ...p(() => import('./app/(dashboard)/settings/help/page')) },
                  { path: 'privacy-policy', ...p(() => import('./app/(dashboard)/settings/privacy-policy/page')) },
                  { path: 'debug-menu', ...p(() => import('./app/(dashboard)/settings/debug-menu/page')) },
                  { path: 'update', ...p(() => import('./app/(dashboard)/settings/update/page')) },
                  { path: 'privacy', ...p(() => import('./app/(dashboard)/settings/privacy/page')) },
                  { path: 'sessions', ...p(() => import('./app/(dashboard)/settings/sessions/page')) },
                  { path: 'security', ...p(() => import('./app/(dashboard)/settings/security/page')) },
                  { path: 'privacy/password', ...p(() => import('./app/(dashboard)/settings/privacy/password/page')) },
                  { path: 'privacy/delete-account', ...p(() => import('./app/(dashboard)/settings/privacy/delete-account/page')) },
                  { path: 'privacy/blocked', ...p(() => import('./app/(dashboard)/settings/privacy/blocked/page')) },
                  { path: 'privacy/sessions', ...p(() => import('./app/(dashboard)/settings/privacy/sessions/page')) },
                  { path: 'privacy/synced-contacts', ...p(() => import('./app/(dashboard)/settings/privacy/synced-contacts/page')) },
                  { path: 'bot', ...p(() => import('./app/(dashboard)/settings/bot/page')) },
                  { path: 'bot/:botId', ...p(() => import('./app/(dashboard)/settings/bot/[botId]/page')) },
                  { path: 'bot-api-tester', ...p(() => import('./app/(dashboard)/settings/bot-api-tester/page')) },
                ],
              },

              // ─── Engagement ─────────────────────────────────────
              { path: 'engagement', ...p(() => import('./app/(dashboard)/engagement/page')) },
              { path: 'engagement/ideas', ...p(() => import('./app/(dashboard)/engagement/ideas/page')) },
              { path: 'engagement/ideas/new', ...p(() => import('./app/(dashboard)/engagement/ideas/new/page')) },
              { path: 'engagement/ideas/leaderboard', ...p(() => import('./app/(dashboard)/engagement/ideas/leaderboard/page')) },
              { path: 'engagement/ideas/:id', ...p(() => import('./app/(dashboard)/engagement/ideas/[id]/page')) },
              { path: 'engagement/news', ...p(() => import('./app/(dashboard)/engagement/news/page')) },
              { path: 'engagement/news/:id', ...p(() => import('./app/(dashboard)/engagement/news/[id]/page')) },
              { path: 'engagement/surveys', ...p(() => import('./app/(dashboard)/engagement/surveys/page')) },
              { path: 'engagement/surveys/:id', ...p(() => import('./app/(dashboard)/engagement/surveys/[id]/page')) },
              { path: 'engagement/surveys/:id/analytics', ...p(() => import('./app/(dashboard)/engagement/surveys/[id]/analytics/page')) },
              { path: 'engagement/surveys/:id/responses', ...p(() => import('./app/(dashboard)/engagement/surveys/[id]/responses/page')) },
              { path: 'engagement/surveys/take/:id', ...p(() => import('./app/(dashboard)/engagement/surveys/take/[id]/page')) },
              { path: 'engagement/wiki', ...p(() => import('./app/(dashboard)/engagement/wiki/page')) },
              { path: 'engagement/wiki/:slug', ...p(() => import('./app/(dashboard)/engagement/wiki/[slug]/page')) },

              // ─── Performance ────────────────────────────────────
              { path: 'performance', ...p(() => import('./app/(dashboard)/performance/page')) },
              { path: 'performance/achievements', ...p(() => import('./app/(dashboard)/performance/achievements/page')) },
              { path: 'performance/achievements/:id', ...p(() => import('./app/(dashboard)/performance/achievements/[id]/page')) },
              { path: 'performance/kpi', ...p(() => import('./app/(dashboard)/performance/kpi/page')) },
              { path: 'performance/kpi/:id', ...p(() => import('./app/(dashboard)/performance/kpi/[id]/page')) },

              // ─── Resources ──────────────────────────────────────
              { path: 'resources/links', ...p(() => import('./app/(dashboard)/resources/links/page')) },

              // ─── Vacations ──────────────────────────────────────
              { path: 'vacations', ...p(() => import('./app/(dashboard)/vacations/page')) },
              { path: 'vacations/:id', ...p(() => import('./app/(dashboard)/vacations/[id]/page')) },

              // ─── Timesheet ──────────────────────────────────────
              { path: 'timesheet', ...p(() => import('./app/(dashboard)/timesheet/page')) },
              { path: 'timesheet/my', ...p(() => import('./app/(dashboard)/timesheet/my/page')) },
              { path: 'timesheet/approve', ...p(() => import('./app/(dashboard)/timesheet/approve/page')) },
              { path: 'timesheet/leave-requests', ...p(() => import('./app/(dashboard)/timesheet/leave-requests/page')) },
              { path: 'timesheet/reports', ...p(() => import('./app/(dashboard)/timesheet/reports/page')) },

              // ─── HR ─────────────────────────────────────────────
              {
                path: 'hr',
                ...l(() => import('./app/(dashboard)/hr/layout')),
                children: [
                  { index: true, ...p(() => import('./app/(dashboard)/hr/page')) },
                  { path: 'colleagues', ...p(() => import('./app/(dashboard)/hr/colleagues/page')) },
                  { path: 'my-achievements', ...p(() => import('./app/(dashboard)/hr/my-achievements/page')) },
                  { path: 'my-kpi', ...p(() => import('./app/(dashboard)/hr/my-kpi/page')) },
                  { path: 'news', ...p(() => import('./app/(dashboard)/hr/news/page')) },
                  { path: 'statistics', ...p(() => import('./app/(dashboard)/hr/statistics/page')) },
                  { path: 'structure', ...p(() => import('./app/(dashboard)/hr/structure/page')) },
                  { path: 'surveys', ...p(() => import('./app/(dashboard)/hr/surveys/page')) },
                  { path: 'vacations', ...p(() => import('./app/(dashboard)/hr/vacations/page')) },
                  { path: 'departments', ...p(() => import('./app/(dashboard)/hr/departments/page')) },
                  { path: 'departments/new', ...p(() => import('./app/(dashboard)/hr/departments/new/page')) },
                  { path: 'departments/:id', ...p(() => import('./app/(dashboard)/hr/departments/[id]/page')) },
                  { path: 'departments/:id/edit', ...p(() => import('./app/(dashboard)/hr/departments/[id]/edit/page')) },
                  { path: 'employees', ...p(() => import('./app/(dashboard)/hr/employees/page')) },
                  { path: 'employees/new', ...p(() => import('./app/(dashboard)/hr/employees/new/page')) },
                  { path: 'employees/:id', ...p(() => import('./app/(dashboard)/hr/employees/[id]/page')) },
                  { path: 'employees/:id/edit', ...p(() => import('./app/(dashboard)/hr/employees/[id]/edit/page')) },
                  { path: 'requests', ...p(() => import('./app/(dashboard)/hr/requests/page')) },
                  { path: 'requests/new', ...p(() => import('./app/(dashboard)/hr/requests/new/page')) },
                  { path: 'requests/pending-approval', ...p(() => import('./app/(dashboard)/hr/requests/pending-approval/page')) },
                  { path: 'requests/:id', ...p(() => import('./app/(dashboard)/hr/requests/[id]/page')) },
                  { path: 'project-logs', ...p(() => import('./app/(dashboard)/hr/project-logs/page')) },
                  { path: 'project-logs/new', ...p(() => import('./app/(dashboard)/hr/project-logs/new/page')) },
                  { path: 'project-logs/:id', ...p(() => import('./app/(dashboard)/hr/project-logs/[id]/page')) },
                  { path: 'archive', ...p(() => import('./app/(dashboard)/hr/archive/page')) },
                  { path: 'archive/:id', ...p(() => import('./app/(dashboard)/hr/archive/[id]/page')) },

                  // Recruitment (nested layout)
                  {
                    path: 'recruitment',
                    ...l(() => import('./app/(dashboard)/hr/recruitment/layout')),
                    children: [
                      { index: true, ...p(() => import('./app/(dashboard)/hr/recruitment/page')) },
                      { path: 'analytics', ...p(() => import('./app/(dashboard)/hr/recruitment/analytics/page')) },
                      { path: 'interviews', ...p(() => import('./app/(dashboard)/hr/recruitment/interviews/page')) },
                      { path: 'candidates', ...p(() => import('./app/(dashboard)/hr/recruitment/candidates/page')) },
                      { path: 'candidates/:id', ...p(() => import('./app/(dashboard)/hr/recruitment/candidates/[id]/page')) },
                      { path: 'candidates/:id/ai-review', ...p(() => import('./app/(dashboard)/hr/recruitment/candidates/[id]/ai-review/page')) },
                      { path: 'jobs', ...p(() => import('./app/(dashboard)/hr/recruitment/jobs/page')) },
                      { path: 'jobs/new', ...p(() => import('./app/(dashboard)/hr/recruitment/jobs/new/page')) },
                      { path: 'jobs/:id', ...p(() => import('./app/(dashboard)/hr/recruitment/jobs/[id]/page')) },
                      { path: 'jobs/:id/applications', ...p(() => import('./app/(dashboard)/hr/recruitment/jobs/[id]/applications/page')) },
                      { path: 'jobs/:id/cases', ...p(() => import('./app/(dashboard)/hr/recruitment/jobs/[id]/cases/page')) },
                      { path: 'jobs/:id/collaborators', ...p(() => import('./app/(dashboard)/hr/recruitment/jobs/[id]/collaborators/page')) },
                    ],
                  },

                  // Rotations (nested layout)
                  {
                    path: 'rotations',
                    ...l(() => import('./app/(dashboard)/hr/rotations/layout')),
                    children: [
                      { index: true, ...p(() => import('./app/(dashboard)/hr/rotations/page')) },
                      { path: 'calendar', ...p(() => import('./app/(dashboard)/hr/rotations/calendar/page')) },
                      { path: 'conflicts', ...p(() => import('./app/(dashboard)/hr/rotations/conflicts/page')) },
                      { path: 'templates', ...p(() => import('./app/(dashboard)/hr/rotations/templates/page')) },
                    ],
                  },
                ],
              },

              // ─── Admin ──────────────────────────────────────────
              {
                path: 'admin',
                ...l(() => import('./app/(dashboard)/admin/layout')),
                children: [
                  { index: true, ...p(() => import('./app/(dashboard)/admin/index/page')) },
                  { path: 'choose-module', ...p(() => import('./app/(dashboard)/admin/choose-module/page')) },
                  {
                    path: 'dashboard',
                    ...l(() => import('./app/(dashboard)/admin/dashboard/layout')),
                    children: [
                      { index: true, element: <Navigate to="real-time" replace /> },
                      { path: 'real-time', ...p(() => import('./app/(dashboard)/admin/dashboard/real-time/page')) },
                      { path: 'historical', ...p(() => import('./app/(dashboard)/admin/dashboard/historical/page')) },
                      { path: 'events', ...p(() => import('./app/(dashboard)/admin/dashboard/events/page')) },
                    ],
                  },
                  { path: 'entities', ...p(() => import('./app/(dashboard)/admin/entities/page')) },
                  { path: 'cdn', ...p(() => import('./app/(dashboard)/admin/cdn/page')) },
                  { path: 'exporter', ...p(() => import('./app/(dashboard)/admin/exporter/page')) },
                  { path: 'links', ...p(() => import('./app/(dashboard)/admin/links/page')) },
                  { path: 'media-player', ...p(() => import('./app/(dashboard)/admin/media-player/page')) },

                  // Admin > Auth (nested layout)
                  {
                    path: 'auth',
                    ...l(() => import('./app/(dashboard)/admin/auth/layout')),
                    children: [
                      { index: true, ...p(() => import('./app/(dashboard)/admin/auth/index/page')) },
                      { path: 'codes', ...p(() => import('./app/(dashboard)/admin/auth/codes/page')) },
                      { path: 'settings', ...p(() => import('./app/(dashboard)/admin/auth/settings/page')) },
                    ],
                  },

                  // Admin > Chat
                  { path: 'chat/moderation', element: <Navigate to="/admin/users/message-reports" replace /> },

                  // Admin > HR
                  { path: 'hr/achievements', ...p(() => import('./app/(dashboard)/admin/hr/achievements/page')) },
                  { path: 'hr/cities', ...p(() => import('./app/(dashboard)/admin/hr/cities/page')) },
                  { path: 'hr/ideas', ...p(() => import('./app/(dashboard)/admin/hr/ideas/page')) },
                  { path: 'hr/kpi', ...p(() => import('./app/(dashboard)/admin/hr/kpi/page')) },
                  { path: 'hr/news', ...p(() => import('./app/(dashboard)/admin/hr/news/page')) },
                  { path: 'hr/news/:id', ...p(() => import('./app/(dashboard)/admin/hr/news/[id]/page')) },
                  { path: 'hr/positions', ...p(() => import('./app/(dashboard)/admin/hr/positions/page')) },
                  { path: 'hr/surveys', ...p(() => import('./app/(dashboard)/admin/hr/surveys/page')) },
                  { path: 'hr/surveys/:id/analytics', ...p(() => import('./app/(dashboard)/admin/hr/surveys/[id]/analytics/page')) },
                  { path: 'hr/surveys/:id/responses', ...p(() => import('./app/(dashboard)/admin/hr/surveys/[id]/responses/page')) },

                  // Admin > Access (nested layout)
                  {
                    path: 'access',
                    ...l(() => import('./app/(dashboard)/admin/access/layout')),
                    children: [
                      { index: true, ...p(() => import('./app/(dashboard)/admin/access/index/page')) },
                      { path: 'overview', ...p(() => import('./app/(dashboard)/admin/access/page')) },
                      { path: 'permissions', ...p(() => import('./app/(dashboard)/admin/access/rules/page')) },
                      { path: 'views', ...p(() => import('./app/(dashboard)/admin/access/views/page')) },
                      { path: 'account-with-roles', ...p(() => import('./app/(dashboard)/admin/access/account-roles/page')) },
                      { path: 'account-roles', ...p(() => import('./app/(dashboard)/admin/access/account-roles/page')) },
                      { path: 'groups', ...p(() => import('./app/(dashboard)/admin/access/groups/page')) },
                      { path: 'ip-policies', ...p(() => import('./app/(dashboard)/admin/access/ip-policies/page')) },
                      { path: 'logs', ...p(() => import('./app/(dashboard)/admin/access/logs/page')) },
                      { path: 'policies', ...p(() => import('./app/(dashboard)/admin/access/policies/page')) },
                      { path: 'roles', ...p(() => import('./app/(dashboard)/admin/access/roles/page')) },
                      { path: 'rules', ...p(() => import('./app/(dashboard)/admin/access/rules/page')) },
                      { path: 'security', ...p(() => import('./app/(dashboard)/admin/access/security/page')) },
                      { path: 'tags', ...p(() => import('./app/(dashboard)/admin/access/tags/page')) },
                    ],
                  },

                  // Admin > Backend (nested layout)
                  {
                    path: 'backend',
                    ...l(() => import('./app/(dashboard)/admin/backend/layout')),
                    children: [
                      { index: true, ...p(() => import('./app/(dashboard)/admin/backend/index/page')) },
                      { path: 'api-keys', ...p(() => import('./app/(dashboard)/admin/backend/api-keys/page')) },
                      { path: 'file-managment', ...p(() => import('./app/(dashboard)/admin/backend/files/page')) },
                      { path: 'files', ...p(() => import('./app/(dashboard)/admin/backend/files/page')) },
                      { path: 'logs', ...p(() => import('./app/(dashboard)/admin/backend/logs/page')) },
                      { path: 'reports', ...p(() => import('./app/(dashboard)/admin/backend/reports/page')) },
                      { path: 'endpoint-logs', ...p(() => import('./app/(dashboard)/admin/backend/endpoint-logs/page')) },
                      { path: 'error-logs', ...p(() => import('./app/(dashboard)/admin/backend/error-logs/page')) },
                      { path: 'navigation-logs', ...p(() => import('./app/(dashboard)/admin/backend/navigation-logs/page')) },
                      { path: 'user-logs', ...p(() => import('./app/(dashboard)/admin/backend/user-logs/page')) },
                      { path: 'upload-file', ...p(() => import('./app/(dashboard)/admin/backend/upload-file/page')) },
                    ],
                  },

                  // Admin > Billing (nested layout)
                  {
                    path: 'billing',
                    ...l(() => import('./app/(dashboard)/admin/billing/layout')),
                    children: [
                      { index: true, element: <Navigate to="dashboard" replace /> },
                      { path: 'dashboard', ...p(() => import('./app/(dashboard)/admin/billing/page')) },
                      { path: 'settings', element: <Navigate to="/admin/billing/settings/descriptions" replace /> },
                      { path: 'settings/descriptions', ...p(() => import('./app/(dashboard)/admin/billing/descriptions/page')) },
                      { path: 'actions', ...p(() => import('./app/(dashboard)/admin/billing/actions/page')) },
                      { path: 'plan/actions', ...p(() => import('./app/(dashboard)/admin/billing/actions/page')) },
                      { path: 'asset-groups', ...p(() => import('./app/(dashboard)/admin/billing/asset-groups/page')) },
                      { path: 'asset-group', ...p(() => import('./app/(dashboard)/admin/billing/asset-groups/page')) },
                      { path: 'assets', ...p(() => import('./app/(dashboard)/admin/billing/assets/page')) },
                      { path: 'assets/:id', ...p(() => import('./app/(dashboard)/admin/billing/assets/[id]/page')) },
                      { path: 'categories', ...p(() => import('./app/(dashboard)/admin/billing/categories/page')) },
                      { path: 'plan/category', ...p(() => import('./app/(dashboard)/admin/billing/categories/page')) },
                      { path: 'charges', ...p(() => import('./app/(dashboard)/admin/billing/charges/page')) },
                      { path: 'usage/charges', ...p(() => import('./app/(dashboard)/admin/billing/charges/page')) },
                      { path: 'descriptions', ...p(() => import('./app/(dashboard)/admin/billing/descriptions/page')) },
                      { path: 'entities', ...p(() => import('./app/(dashboard)/admin/billing/entities/page')) },
                      { path: 'jurisdictions', ...p(() => import('./app/(dashboard)/admin/billing/jurisdictions/page')) },
                      { path: 'entity/jurisdiction', ...p(() => import('./app/(dashboard)/admin/billing/jurisdictions/page')) },
                      { path: 'plans', ...p(() => import('./app/(dashboard)/admin/billing/plans/page')) },
                      { path: 'providers', ...p(() => import('./app/(dashboard)/admin/billing/providers/page')) },
                      { path: 'plan/provider', ...p(() => import('./app/(dashboard)/admin/billing/providers/page')) },
                      { path: 'routes', ...p(() => import('./app/(dashboard)/admin/billing/routes/page')) },
                      { path: 'service/route', ...p(() => import('./app/(dashboard)/admin/billing/routes/page')) },
                      { path: 'services', ...p(() => import('./app/(dashboard)/admin/billing/services/page')) },
                      { path: 'trunks', ...p(() => import('./app/(dashboard)/admin/billing/trunks/page')) },
                      { path: 'service/route-trunk', ...p(() => import('./app/(dashboard)/admin/billing/trunks/page')) },
                      { path: 'usage', ...p(() => import('./app/(dashboard)/admin/billing/usage/page')) },
                      { path: 'usages', ...p(() => import('./app/(dashboard)/admin/billing/usage/page')) },
                    ],
                  },

                  // Admin > Marketing
                  {
                    path: 'marketing',
                    children: [
                      { index: true, element: <Navigate to="campaigns" replace /> },
                      { path: 'campaigns', ...p(() => import('./app/(dashboard)/admin/marketing/campaigns/page')) },
                      { path: 'segments', ...p(() => import('./app/(dashboard)/admin/marketing/segments/page')) },
                      { path: 'automations', ...p(() => import('./app/(dashboard)/admin/marketing/automations/page')) },
                      { path: 'analytics', ...p(() => import('./app/(dashboard)/admin/marketing/analytics/page')) },
                    ],
                  },

                  // Admin > Telephony (nested layout with sidebar)
                  {
                    path: 'telephony',
                    ...l(() => import('./app/(dashboard)/admin/telephony/layout')),
                    children: [
                      { index: true, element: <Navigate to="trunks" replace /> },
                      { path: 'trunks', ...p(() => import('./app/(dashboard)/admin/telephony/trunks/page')) },
                      { path: 'trunks/create', ...p(() => import('./app/(dashboard)/admin/telephony/trunks/create/page')) },
                      { path: 'trunks/:id', ...p(() => import('./app/(dashboard)/admin/telephony/trunks/[id]/page')) },
                      { path: 'routes', ...p(() => import('./app/(dashboard)/admin/telephony/routes/page')) },
                      { path: 'routes/:id', ...p(() => import('./app/(dashboard)/admin/telephony/routes/[id]/page')) },
                      { path: 'rules', ...p(() => import('./app/(dashboard)/admin/telephony/rules/page')) },
                      { path: 'rules/create', ...p(() => import('./app/(dashboard)/admin/telephony/rules/create/page')) },
                      { path: 'rules/:id', ...p(() => import('./app/(dashboard)/admin/telephony/rules/[id]/page')) },
                      { path: 'flows', ...p(() => import('./app/(dashboard)/admin/telephony/flows/page')) },
                      { path: 'tracing', ...p(() => import('./app/(dashboard)/admin/telephony/tracing/page')) },
                      { path: 'lims', ...p(() => import('./app/(dashboard)/admin/telephony/lims/page')) },
                      { path: 'route-tester', ...p(() => import('./app/(dashboard)/admin/telephony/route-tester/page')) },
                    ],
                  },

                  // Admin > CDR (nested layout)
                  {
                    path: 'cdr',
                    ...l(() => import('./app/(dashboard)/admin/cdr/layout')),
                    children: [
                      { index: true, ...p(() => import('./app/(dashboard)/admin/cdr/page')) },
                      { path: 'alerts', ...p(() => import('./app/(dashboard)/admin/cdr/alerts/page')) },
                      { path: 'mapping-rules', ...p(() => import('./app/(dashboard)/admin/cdr/mapping-rules/page')) },
                      { path: 'quality', ...p(() => import('./app/(dashboard)/admin/cdr/quality/page')) },
                      { path: 'route-tables', ...p(() => import('./app/(dashboard)/admin/cdr/route-tables/page')) },
                      { path: 'scheduled-exports', ...p(() => import('./app/(dashboard)/admin/cdr/scheduled-exports/page')) },
                      { path: 'history', ...p(() => import('./app/(dashboard)/admin/cdr/history/page')) },
                      { path: 'metrics', ...p(() => import('./app/(dashboard)/admin/cdr/metrics/page')) },
                      { path: 'records', ...p(() => import('./app/(dashboard)/admin/cdr/records/page')) },
                      { path: 'call-dashboard', ...p(() => import('./app/(dashboard)/admin/cdr/call-dashboard/page')) },
                    ],
                  },

                  // Admin > CRM (nested layout)
                  {
                    path: 'crm',
                    ...l(() => import('./app/(dashboard)/admin/crm/layout')),
                    children: [
                      { index: true, ...p(() => import('./app/(dashboard)/admin/crm/page')) },
                      { path: 'dashboard', ...p(() => import('./app/(dashboard)/admin/crm/dashboard/page')) },
                      { path: 'members', ...p(() => import('./app/(dashboard)/admin/crm/members/page')) },
                      { path: 'customers', ...p(() => import('./app/(dashboard)/admin/crm/customers/page')) },
                      { path: 'customers/:id', ...p(() => import('./app/(dashboard)/admin/crm/customers/[id]/page')) },
                      { path: 'interactions', ...p(() => import('./app/(dashboard)/admin/crm/interactions/page')) },
                      { path: 'knowledge', ...p(() => import('./app/(dashboard)/admin/crm/knowledge/page')) },
                      { path: 'audit', ...p(() => import('./app/(dashboard)/admin/crm/audit/page')) },
                      { path: 'callbacks', ...p(() => import('./app/(dashboard)/admin/crm/callbacks/page')) },
                      { path: 'pipeline', ...p(() => import('./app/(dashboard)/admin/crm/pipeline/page')) },
                      { path: 'reports', ...p(() => import('./app/(dashboard)/admin/crm/reports/page')) },
                      { path: 'leaderboard', ...p(() => import('./app/(dashboard)/admin/crm/leaderboard/page')) },
                      { path: 'tasks', ...p(() => import('./app/(dashboard)/admin/crm/tasks/page')) },
                      { path: 'settings', ...p(() => import('./app/(dashboard)/admin/crm/settings/page')) },
                      { path: 'settings/business', element: <Navigate to="/admin/crm/settings/crm-business" replace /> },
                      { path: 'settings/channels', element: <Navigate to="/admin/crm/settings/crm-channels" replace /> },
                      { path: 'settings/crm-business', ...p(() => import('./app/(dashboard)/admin/crm/settings/business/page')) },
                      { path: 'settings/crm-channels', ...p(() => import('./app/(dashboard)/admin/crm/settings/channels/page')) },
                      { path: 'settings/crm-queue', ...p(() => import('./app/(dashboard)/admin/crm/settings/queues/page')) },
                      { path: 'settings/queue-monitor', ...p(() => import('./app/(dashboard)/admin/crm/settings/queue-monitor/page')) },
                      { path: 'settings/form-fields', ...p(() => import('./app/(dashboard)/admin/crm/settings/form-fields/page')) },
                      { path: 'settings/crm-sla', ...p(() => import('./app/(dashboard)/admin/crm/settings/sla/page')) },
                      { path: 'settings/crm-tag', ...p(() => import('./app/(dashboard)/admin/crm/settings/tags/page')) },
                      { path: 'settings/flow-audio', ...p(() => import('./app/(dashboard)/admin/crm/settings/flow-audio/page')) },
                      { path: 'settings/flow-ivr', ...p(() => import('./app/(dashboard)/admin/crm/settings/flow-ivr/page')) },
                      { path: 'settings/flow-ivr/:id', ...p(() => import('./app/(dashboard)/admin/crm/settings/flow-ivr/[id]/page')) },
                      { path: 'settings/queues', element: <Navigate to="/admin/crm/settings/crm-queue" replace /> },
                      { path: 'settings/sla', element: <Navigate to="/admin/crm/settings/crm-sla" replace /> },
                      { path: 'settings/tags', element: <Navigate to="/admin/crm/settings/crm-tag" replace /> },
                      { path: 'settings/dnc', ...p(() => import('./app/(dashboard)/admin/crm/settings/dnc/page')) },
                      { path: 'settings/responses', ...p(() => import('./app/(dashboard)/admin/crm/settings/responses/page')) },
                      { path: 'settings/escalation', ...p(() => import('./app/(dashboard)/admin/crm/settings/escalation/page')) },
                      { path: 'settings/form-submissions', ...p(() => import('./app/(dashboard)/admin/crm/settings/form-submissions/page')) },
                      { path: 'settings/bots', ...p(() => import('./app/(dashboard)/admin/crm/settings/bots/page')) },
                      { path: 'settings/ai-providers', ...p(() => import('./app/(dashboard)/admin/crm/settings/ai-providers/page')) },
                      { path: 'settings/queue-alerts', ...p(() => import('./app/(dashboard)/admin/crm/settings/queue-alerts/page')) },
                      { path: 'settings/interaction-statuses', ...p(() => import('./app/(dashboard)/admin/crm/settings/interaction-statuses/page')) },
                      { path: 'settings/task-templates', ...p(() => import('./app/(dashboard)/admin/crm/settings/task-templates/page')) },
                      { path: 'settings/companies', ...p(() => import('./app/(dashboard)/admin/crm/settings/companies/page')) },
                    ],
                  },

                  // Admin > Documents (nested layout)
                  {
                    path: 'documents',
                    ...l(() => import('./app/(dashboard)/admin/documents/layout')),
                    children: [
                      { index: true, ...p(() => import('./app/(dashboard)/admin/documents/page')) },
                      { path: ':id', ...p(() => import('./app/(dashboard)/admin/documents/[id]/page')) },
                      { path: 'approvals', ...p(() => import('./app/(dashboard)/admin/documents/approvals/page')) },
                      { path: 'folders', ...p(() => import('./app/(dashboard)/admin/documents/folders/page')) },
                      { path: 'ml-profiles', ...p(() => import('./app/(dashboard)/admin/documents/ml-profiles/page')) },
                      { path: 'set-annotations', ...p(() => import('./app/(dashboard)/admin/documents/set-annotations/page')) },
                      { path: 'set-groups', ...p(() => import('./app/(dashboard)/admin/documents/set-groups/page')) },
                      { path: 'sets', ...p(() => import('./app/(dashboard)/admin/documents/sets/page')) },
                      { path: 'shared', ...p(() => import('./app/(dashboard)/admin/documents/shared/page')) },
                      { path: 'templates', ...p(() => import('./app/(dashboard)/admin/documents/templates/page')) },
                      { path: 'type-annotations', ...p(() => import('./app/(dashboard)/admin/documents/type-annotations/page')) },
                      { path: 'types', ...p(() => import('./app/(dashboard)/admin/documents/types/page')) },
                    ],
                  },

                  // Admin > LIMS (nested layout)
                  {
                    path: 'lims',
                    ...l(() => import('./app/(dashboard)/admin/lims/layout')),
                    children: [
                      { index: true, ...p(() => import('./app/(dashboard)/admin/lims/page')) },
                      { path: 'accounts', ...p(() => import('./app/(dashboard)/admin/lims/accounts/page')) },
                      { path: 'phone-numbers', ...p(() => import('./app/(dashboard)/admin/lims/phone-numbers/page')) },
                    ],
                  },

                  // Admin > Media (nested layout)
                  {
                    path: 'media',
                    ...l(() => import('./app/(dashboard)/admin/media/layout')),
                    children: [
                      { index: true, ...p(() => import('./app/(dashboard)/admin/media/page')) },
                      { path: 'countries', ...p(() => import('./app/(dashboard)/admin/media/countries/page')) },
                      { path: 'departments', ...p(() => import('./app/(dashboard)/admin/media/departments/page')) },
                      { path: 'genres', ...p(() => import('./app/(dashboard)/admin/media/genres/page')) },
                      { path: 'jobs', ...p(() => import('./app/(dashboard)/admin/media/jobs/page')) },
                      { path: 'live-tv', ...p(() => import('./app/(dashboard)/admin/media/live-tv/page')) },
                      { path: 'movies', ...p(() => import('./app/(dashboard)/admin/media/movies/page')) },
                      { path: 'music', ...p(() => import('./app/(dashboard)/admin/media/music/page')) },
                      { path: 'page-categories', ...p(() => import('./app/(dashboard)/admin/media/page-categories/page')) },
                      { path: 'page-groups', ...p(() => import('./app/(dashboard)/admin/media/page-groups/page')) },
                      { path: 'persons', ...p(() => import('./app/(dashboard)/admin/media/persons/page')) },
                      { path: 'platforms', ...p(() => import('./app/(dashboard)/admin/media/platforms/page')) },
                      { path: 'radio', ...p(() => import('./app/(dashboard)/admin/media/radio/page')) },
                      { path: 'search', ...p(() => import('./app/(dashboard)/admin/media/search/page')) },
                      { path: 'series', ...p(() => import('./app/(dashboard)/admin/media/series/page')) },
                      { path: 'studios', ...p(() => import('./app/(dashboard)/admin/media/studios/page')) },
                      { path: 'tags', ...p(() => import('./app/(dashboard)/admin/media/tags/page')) },
                    ],
                  },

                  // Admin > Users (nested layout)
                  {
                    path: 'users',
                    ...l(() => import('./app/(dashboard)/admin/users/layout')),
                    children: [
                      { index: true, element: <Navigate to="search" replace /> },
                      { path: 'search', ...p(() => import('./app/(dashboard)/admin/users/page')) },
                      { path: 'roles', ...p(() => import('./app/(dashboard)/admin/access/account-roles/page')) },
                      { path: 'tags', ...p(() => import('./app/(dashboard)/admin/users/tags/page')) },
                      { path: 'message-reports', ...p(() => import('./app/(dashboard)/admin/users/message-reports/page')) },
                      {
                        path: ':id',
                        ...l(() => import('./app/(dashboard)/admin/users/[id]/layout')),
                        children: [
                          { index: true, ...p(() => import('./app/(dashboard)/admin/users/[id]/page')) },
                          { path: 'info', ...p(() => import('./app/(dashboard)/admin/users/[id]/info/page')) },
                          { path: 'activity-logs', ...p(() => import('./app/(dashboard)/admin/users/[id]/activity-logs/page')) },
                          { path: 'applications', ...p(() => import('./app/(dashboard)/admin/users/[id]/applications/page')) },
                          { path: 'call-history', ...p(() => import('./app/(dashboard)/admin/users/[id]/call-history/page')) },
                          { path: 'call-profile', ...p(() => import('./app/(dashboard)/admin/users/[id]/call-profile/page')) },
                          { path: 'interactions', ...p(() => import('./app/(dashboard)/admin/users/[id]/interactions/page')) },
                          { path: 'sessions', ...p(() => import('./app/(dashboard)/admin/users/[id]/sessions/page')) },
                          { path: 'storage', ...p(() => import('./app/(dashboard)/admin/users/[id]/storage/page')) },
                          { path: 'wallet', ...p(() => import('./app/(dashboard)/admin/users/[id]/wallet/page')) },
                          { path: 'wallet-intents', ...p(() => import('./app/(dashboard)/admin/users/[id]/wallet-intents/page')) },
                          { path: 'wallet-transactions', ...p(() => import('./app/(dashboard)/admin/users/[id]/wallet-transactions/page')) },
                          { path: 'wallet-transfers', ...p(() => import('./app/(dashboard)/admin/users/[id]/wallet-transfers/page')) },
                          { path: 'billing', ...p(() => import('./app/(dashboard)/admin/users/[id]/billing/page')) },
                          { path: 'billing/:planId', ...p(() => import('./app/(dashboard)/admin/users/[id]/billing/[planId]/page')) },
                        ],
                      },
                      {
                        path: 'profile/:id',
                        ...l(() => import('./app/(dashboard)/admin/users/[id]/layout')),
                        children: [
                          { index: true, ...p(() => import('./app/(dashboard)/admin/users/[id]/page')) },
                          { path: 'info', ...p(() => import('./app/(dashboard)/admin/users/[id]/info/page')) },
                          { path: 'activity-logs', ...p(() => import('./app/(dashboard)/admin/users/[id]/activity-logs/page')) },
                          { path: 'applications', ...p(() => import('./app/(dashboard)/admin/users/[id]/applications/page')) },
                          { path: 'call-history', ...p(() => import('./app/(dashboard)/admin/users/[id]/call-history/page')) },
                          { path: 'call-profile', ...p(() => import('./app/(dashboard)/admin/users/[id]/call-profile/page')) },
                          { path: 'interactions', ...p(() => import('./app/(dashboard)/admin/users/[id]/interactions/page')) },
                          { path: 'sessions', ...p(() => import('./app/(dashboard)/admin/users/[id]/sessions/page')) },
                          { path: 'storage', ...p(() => import('./app/(dashboard)/admin/users/[id]/storage/page')) },
                          { path: 'wallet', ...p(() => import('./app/(dashboard)/admin/users/[id]/wallet/page')) },
                          { path: 'wallet-intents', ...p(() => import('./app/(dashboard)/admin/users/[id]/wallet-intents/page')) },
                          { path: 'wallet-transactions', ...p(() => import('./app/(dashboard)/admin/users/[id]/wallet-transactions/page')) },
                          { path: 'wallet-transfers', ...p(() => import('./app/(dashboard)/admin/users/[id]/wallet-transfers/page')) },
                          { path: 'billing', ...p(() => import('./app/(dashboard)/admin/users/[id]/billing/page')) },
                          { path: 'billing/:planId', ...p(() => import('./app/(dashboard)/admin/users/[id]/billing/[planId]/page')) },
                        ],
                      },
                    ],
                  },

                  // Admin > Wallet (nested layout)
                  {
                    path: 'wallet',
                    ...l(() => import('./app/(dashboard)/admin/wallet/layout')),
                    children: [
                      { index: true, ...p(() => import('./app/(dashboard)/admin/wallet/index/page')) },
                      { path: 'dashboard', ...p(() => import('./app/(dashboard)/admin/wallet/page')) },
                      { path: 'conversions', ...p(() => import('./app/(dashboard)/admin/wallet/conversions/page')) },
                      { path: 'descriptions', ...p(() => import('./app/(dashboard)/admin/wallet/descriptions/page')) },
                      { path: 'audit', ...p(() => import('./app/(dashboard)/admin/wallet/audit/page')) },
                      { path: 'intents', ...p(() => import('./app/(dashboard)/admin/wallet/intents/page')) },
                      { path: 'sources', ...p(() => import('./app/(dashboard)/admin/wallet/sources/page')) },
                      { path: 'system-profiles', ...p(() => import('./app/(dashboard)/admin/wallet/system-profiles/page')) },
                      { path: 'transactions', ...p(() => import('./app/(dashboard)/admin/wallet/transactions/page')) },
                      { path: 'transactions/:id', ...p(() => import('./app/(dashboard)/admin/wallet/transactions/[id]/page')) },
                      { path: 'transfers', ...p(() => import('./app/(dashboard)/admin/wallet/transfers/page')) },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },

      // ─── Public routes (no auth required) ─────────────────────────
      {
        path: 'privacy-policy',
        ...l(() => import('./app/(public)/privacy-policy/layout')),
        children: [
          { index: true, ...p(() => import('./app/(dashboard)/settings/privacy-policy/page')) },
        ],
      },
      {
        path: 'terms-of-use',
        ...l(() => import('./app/(public)/terms-of-use/layout')),
        children: [
          { index: true, ...p(() => import('./app/(public)/terms-of-use/page')) },
        ],
      },
      {
        path: 'call',
        ...l(() => import('./app/(public)/call/layout')),
        children: [
          { path: 'create', ...p(() => import('./app/(public)/call/create/page')) },
          { path: 'alert/:id', ...p(() => import('./app/(public)/call/alert/[id]/page')) },
          // Legacy join routes removed — use /conference/link/:id instead
        ],
      },
      {
        path: 'l',
        ...l(() => import('./app/(public)/l/layout')),
        children: [
          { path: ':code', ...p(() => import('./app/(public)/l/[code]/page')) },
        ],
      },

      // ─── 404 fallback ─────────────────────────────────────────────
      {
        path: '*',
        ...p(() => import('./app/not-found')),
      },
    ],
  },
], {
  basename: import.meta.env.BASE_URL.replace(/\/$/, ''),
})
